import React, { useState } from 'react';
import { Globe, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { publishAssessment } from '../../services/reviewWorkflowApi';

/**
 * Publish confirmation modal for an assessment (FECOA — publish flow).
 *
 * Shared by every place a publish can be triggered — the card's
 * ReviewWorkflowActions, the Overview Hub "Ready to publish" queue, and the
 * Manage modal — so publishing always goes through one confirm + one API call.
 *
 * Publishing creates a locked version snapshot and flips the assessment live,
 * so we capture a change summary and allow an optional version override
 * (matching the backend publish action). The backend re-validates, so a stale
 * trigger surfaces as a toast rather than a broken state.
 *
 * Props:
 *   assessment  - the assessment object (needs uuid|id + name)
 *   onClose     - close the modal without publishing
 *   onPublished - called with the API result after a successful publish
 */
const PublishAssessmentModal = ({ assessment, onClose, onPublished }) => {
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);

  const uuid = assessment?.uuid || assessment?.id;
  const name = assessment?.name || assessment?.survey?.name || 'this assessment';

  const errorMessage = (err) => {
    const data = err?.response?.data;
    if (Array.isArray(data?.errors) && data.errors.length) {
      return `${data.detail || data.error || 'Could not publish.'} ${data.errors.join(' ')}`;
    }
    return data?.detail || data?.error || 'Could not publish this assessment.';
  };

  const confirm = async () => {
    if (!uuid) return;
    setBusy(true);
    try {
      const result = await publishAssessment(uuid, {
        change_summary: summary.trim(),
      });
      toast.success('Assessment published.');
      onPublished && onPublished(result);
      onClose && onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50 p-4 text-left whitespace-normal"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose && onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 pt-6 pb-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-600" />
            Publish Assessment
          </h3>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mt-2">{name}</p>
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              Publishing locks a new version snapshot and makes this assessment{' '}
              <strong>available for use in studies</strong>. Once published, only an
              administrator can revert it to a draft.
            </span>
          </div>
        </div>

        <div className="px-6 pb-2 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">
              Change summary <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              autoFocus
              placeholder="e.g. Final copy edits to screens 2–4; ready for study use."
              className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm"
            />
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
          >
            <Globe className="w-4 h-4" />
            {busy ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishAssessmentModal;
